<template>
  <StrapiContentWrapper v-if="!parsed" :rich-text="richText" />
  <div v-else data-test-id="event-tour-steps">
    <div
      v-if="parsed.preambleHtml"
      class="event-tour-steps__preamble mb-4 text-sm font-normal leading-[22px] text-stone-500"
      data-test-id="event-tour-steps-preamble"
      v-html="parsed.preambleHtml"
    />
    <div class="border-t border-b border-stone-200">
      <div class="grid grid-cols-1 md:grid-cols-2">
        <ol
          v-for="(column, columnIndex) in columns"
          :key="columnIndex"
          class="m-0 list-none p-0"
          :data-test-id="
            columnIndex === 0
              ? 'event-tour-steps-left'
              : 'event-tour-steps-right'
          "
        >
          <li
            v-for="step in column"
            :key="step.number"
            class="flex items-start gap-3 px-0 py-4 md:px-4"
            :data-test-id="`event-tour-step-${step.number}`"
          >
            <span
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-teal-500 text-sm font-bold text-white"
              aria-hidden="true"
            >
              {{ step.number }}
            </span>
            <div
              class="min-w-0 flex-1 self-center text-sm leading-[22px] text-stone-600"
            >
              <HtmlReadMore
                :html="step.html"
                :test-id-prefix="`event-tour-step-${step.number}`"
              />
            </div>
          </li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { parseHtmlList } from '~/utils/parseHtmlList'

interface TourStep {
  number: number
  html: string
}

const { richText } = defineProps<{
  richText: string
}>()

const parsed = computed(() => parseHtmlList(richText))

const steps = computed<TourStep[]>(() => {
  if (!parsed.value) {
    return []
  }

  return parsed.value.items.map((html, index) => ({
    number: index + 1,
    html,
  }))
})

const columns = computed(() => {
  if (!steps.value.length) {
    return []
  }

  const midpoint = Math.ceil(steps.value.length / 2)
  const left = steps.value.slice(0, midpoint)
  const right = steps.value.slice(midpoint)

  return right.length > 0 ? [left, right] : [left]
})
</script>

<style scoped lang="postcss">
.event-tour-steps__preamble :deep(p) {
  @apply m-0;
}

.event-tour-steps__preamble :deep(p + p) {
  @apply mt-2 block;
}
</style>
